import {
  ClientBrandingConfig,
  ClientConfigRegistry,
} from '../types/clientConfig';
import { CLIENTS } from './types';
import revolutionsConfig from './revolution';
import jazzxConfig from './jazzx';
import { CLIENT_ID } from '../env';

/**
 * Client Configuration Registry
 * Central registry for all client-specific configurations
 */
const clientConfigRegistry: ClientConfigRegistry = {
  [CLIENTS.REVOLUTION]: revolutionsConfig,
  [CLIENTS.JAZZX]: jazzxConfig,
  // Add other clients here as they are created
};

/**
 * Get client configuration by client ID
 * @param clientId - The client identifier
 * @returns Client branding configuration or null if not found
 */
export const getClientConfig = (
  clientId: string | undefined
): ClientBrandingConfig | null => {
  if (!clientId) return null;
  return clientConfigRegistry[clientId] || null;
};

/**
 * Get the current active client configuration
 * This can be determined by environment variable, URL subdomain, or other logic
 * @returns Active client configuration
 */
export const getCurrentClientConfig = (): ClientBrandingConfig => {
  // Primary: Get client from environment variable
  const currentClientId = CLIENT_ID; // import.meta.env.VITE_CLIENT_ID;

  // Alternative: Get client from subdomain
  // const subdomain = window.location.hostname.split('.')[0];
  // const currentClientId = subdomain === 'localhost' ? 'revolutions' : subdomain;

  const config = getClientConfig(currentClientId);

  if (!config) {
    console.warn(
      `Client configuration not found for: ${currentClientId}. Falling back to ${CLIENTS.REVOLUTION}.`
    );
    return revolutionsConfig;
  }

  return config;
};

/**
 * Apply client configuration to the document
 * @param config - Client branding configuration
 */
export const applyClientConfig = (config: ClientBrandingConfig): void => {
  console.log('Client configuration applied:', {
    clientId: config.clientId,
    clientName: config.clientName,
  });
};

/**
 * Get all available client IDs
 * @returns Array of client identifiers
 */
export const getAvailableClients = (): string[] => {
  return Object.keys(clientConfigRegistry);
};

/**
 * Check if a client configuration exists
 * @param clientId - The client identifier
 * @returns True if client exists, false otherwise
 */
export const hasClientConfig = (clientId: string): boolean => {
  return clientId in clientConfigRegistry;
};

// Export individual configs for direct import
export { revolutionsConfig, jazzxConfig };

// Export types
export type { ClientBrandingConfig, ClientConfigRegistry };
